import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import PostgRESTTable from "../shared/PostgRESTTable";
import MarketingPersonaForm from "./MarketingPersonaForm";

// constants
const PAGE_SIZE = 10;

function MarketingPersonasIndex() {
  const [showModal, setShowModal] = useState(false);
  const [editingPersona, setEditingPersona] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const marketingPersonaFormRef = useRef();

  // Memoize headers to prevent unnecessary re-renders of PostgRESTTable
  const memoizedHeaders = useMemo(() => authenticatedHeadersHash(), []);

  const handleModalOpen = useCallback(() => {
    setEditingPersona(null);
    setShowModal(true);
  }, []);

  const handleEditPersona = useCallback((persona) => {
    setEditingPersona(persona);
    setShowModal(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setEditingPersona(null);
    setIsFormSubmitting(false);
  }, []);

  const triggerTableRefresh = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  const handleFormSubmit = useCallback(() => {
    if (marketingPersonaFormRef.current) {
      marketingPersonaFormRef.current.submit();
    }
  }, []);

  const handleDeletePersona = useCallback(
    async (persona) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Are you sure you want to delete ${persona.full_name}?`)) {
        return;
      }

      try {
        const response = await fetch(`/admin/marketing_personas/${persona.id}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.getAttribute("content"),
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        triggerTableRefresh();
      } catch (error) {
        // eslint-disable-next-line no-alert
        window.alert(`Error deleting persona: ${error.message}`);
      }
    },
    [triggerTableRefresh],
  );

  // Track form submission state for button updates
  useEffect(() => {
    const interval = setInterval(() => {
      if (marketingPersonaFormRef.current) {
        setIsFormSubmitting(marketingPersonaFormRef.current.isSubmitting);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [showModal]);

  return (
    <div>
      <PostgRESTTable
        endpoint={`${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1`}
        resource="marketing_personas"
        columns={{
          id: { type: "number", hidden: true }, // Hidden field needed for editing/deleting
          team: { type: "string", hidden: true }, // Hidden field needed for editing
          full_name: { type: "string", display_as: "name" },
          first_name: { type: "string", hidden: true }, // Hidden field needed for editing
          last_name: { type: "string", hidden: true }, // Hidden field needed for editing
          email: { type: "string" },
          created_at: { type: "datetime" },
        }}
        searchColumns={["full_name", "email"]}
        pageSize={PAGE_SIZE}
        headers={memoizedHeaders}
        defaultSort={{ full_name: "asc" }}
        refreshTrigger={refreshTrigger}
        renderActions={(row) => (
          <div className="d-flex gap-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => handleEditPersona(row)}
              title="Edit persona"
            >
              <i className="fa-solid fa-pen" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeletePersona(row)}
              title="Delete persona"
            >
              <i className="fa-solid fa-trash" aria-hidden="true" />
            </button>
          </div>
        )}
        toolbar={
          <Button variant="primary" onClick={handleModalOpen}>
            <i className="fa-solid fa-plus me-1" /> Create Persona
          </Button>
        }
      />

      {/* Create/Edit Marketing Persona Modal */}
      <Modal show={showModal} size="md" onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{editingPersona ? "Edit Persona" : "Create Persona"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <MarketingPersonaForm
            ref={marketingPersonaFormRef}
            persona={editingPersona}
            onClose={handleModalClose}
            onSuccess={triggerTableRefresh}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="link" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleFormSubmit} disabled={isFormSubmitting}>
            {(() => {
              if (isFormSubmitting) {
                return (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {editingPersona ? "Updating..." : "Creating..."}
                  </>
                );
              }
              return editingPersona ? "Update" : "Create";
            })()}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MarketingPersonasIndex;
